import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { ChevronRight } from '@lifeomic/chromicons';
import { useInProgressSurveyStart } from '@lifeomic/phc-web-toolkit/src/components/Surveys/hooks/useInProgressSurveyStart';
import InProgressSurveyChoiceModal from '@lifeomic/phc-web-toolkit/src/components/Surveys/Components/InProgressSurveyChoiceModal';

const messages = defineMessages({
  openTooltip: {
    id: 'subjectViewer.manageSurveys.openTooltip',
    defaultMessage: 'Begin survey',
  },
  viewSurvey: {
    id: 'patientViewer.internalSurveys.columns.viewSurvey',
    defaultMessage: 'View Survey',
  },
  loadingTooltip: {
    id: 'subjectViewer.manageSurveys.loadingResponsesTooltip',
    defaultMessage: 'Loading in-progress responses',
  },
  responsesErrorTooltip: {
    id: 'subjectViewer.manageSurveys.responsesErrorTooltip',
    defaultMessage:
      'Unable to load in-progress responses. Refresh before starting a survey.',
  },
});

export type BeginSurveyButtonProps = {
  survey: fhir.Questionnaire;
  responses: fhir.QuestionnaireResponse[] | undefined;
  areResponsesReady?: boolean;
  hasResponsesError?: boolean;
  currentUserId?: string;
  onStartNew: (survey: fhir.Questionnaire) => void;
  onContinue: (
    survey: fhir.Questionnaire,
    response: fhir.QuestionnaireResponse,
  ) => void;
};

export const BeginSurveyButton: React.FC<BeginSurveyButtonProps> = ({
  survey,
  responses,
  areResponsesReady = true,
  hasResponsesError = false,
  currentUserId,
  onStartNew,
  onContinue,
}) => {
  const { formatMessage } = useIntl();
  const {
    isPromptOpen,
    pendingDrafts,
    requestStart,
    dismiss,
    confirmContinue,
    confirmStartNew,
  } = useInProgressSurveyStart({
    responses,
    areResponsesReady,
    onStartNew,
    onContinue,
  });

  if (!survey.id) {
    return null;
  }

  const unavailableTooltip = hasResponsesError
    ? messages.responsesErrorTooltip
    : messages.loadingTooltip;

  return (
    <>
      <Tooltip
        title={formatMessage(
          areResponsesReady ? messages.openTooltip : unavailableTooltip,
        )}
      >
        <IconButton
          data-survey-id={survey.id}
          aria-label={formatMessage(messages.viewSurvey)}
          color="inverse"
          icon={ChevronRight}
          disabled={!areResponsesReady}
          onClick={e => {
            e.stopPropagation();
            e.currentTarget.blur();
            requestStart(survey);
          }}
        />
      </Tooltip>
      <InProgressSurveyChoiceModal
        isOpen={isPromptOpen}
        drafts={pendingDrafts}
        currentUserId={currentUserId}
        onDismiss={dismiss}
        onContinue={confirmContinue}
        onStartNew={confirmStartNew}
      />
    </>
  );
};
